import * as React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-react';

import { CookieError, Loading } from 'components';
import { ROUTES } from 'consts';
import { useFeatureToggles } from 'hooks/unleash.hook';
import {
  AuthNavigator,
  BuildingDetailsNavigator,
  DashboardNavigator,
  ProfileNavigator,
  SupervisorNavigator,
  UnauthNavigator,
  VerificationNavigator,
  ZonesNavigator,
} from 'navigators';
import {
  AuthForgotPasswordPage,
  AuthLoginPage,
  AuthResetPasswordPage,
  AuthSignupPage,
  BuildingCreatePage,
  BuildingsPage,
  BuildingZonesActivePage,
  BuildingZonesInactivePage,
  CartPageV2,
  DashboardPage,
  EmployeesPage,
  FeedbackPage,
  HomePage,
  PdfViewPage,
  ProfileBillingPage,
  ProfileCompanySettingsPage,
  ProfileInviteUsersPage,
  ProfileNotificationsPage,
  ProfilePasswordPage,
  ProfileUserSettingsPage,
  ReportsPage,
  ServiceInterruptionsPage,
  TeamPage,
  VerificationConfirmPage,
  VerificationRequestPage,
  ZoneCategoriesPage,
  ZoneCleaningHistoryPage,
  ZonesActivePage,
  ZonesInactivePage,
  SuspendServicePage,
  SuspendServiceSuccessPage,
  ReviewCartPage,
} from 'pages';
import { useGetXsrfTokenQuery } from 'store/apis/cookie.api';
import { useFetchUserQuery } from 'store/apis/user.api';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo, selectEmployeeCode } from 'store/slices/auth.slice';
import { updateLayout } from 'store/slices/layout.slice';
import { CodePage, EmployeePage } from '../pages/code';
import { CodeNavigator } from './code.navigator';
import { OrderDecalsPage } from '../pages/decals/order-decals.page';

interface RootNavigatorProps {}

export const RootNavigator: React.FunctionComponent<RootNavigatorProps> = () => {
  const { isError, isLoading, isSuccess } = useGetXsrfTokenQuery();
  const dispatch = useAppDispatch();
  const { orgSlug, isAdmin, isLoggedIn } = useAppSelector(selectCurrentUserInfo);
  const employeeCode = useAppSelector(selectEmployeeCode);
  useFetchUserQuery(undefined, { skip: !isLoggedIn });
  const updateContext = useUnleashContext();
  const { flagsReady } = useFlagsStatus();
  const { dashboard, serviceInterruptions } = useFeatureToggles();

  const handleScroll = () => {
    const scrollX = window.pageXOffset;
    const scrollY = window.pageYOffset;

    dispatch(updateLayout({ scrollX, scrollY }));
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  React.useEffect(() => {
    if (!updateContext) {
      return;
    }

    updateContext({ properties: { organizationId: orgSlug } });
  }, [updateContext, orgSlug]);

  if (isLoading) {
    return <CookieLoading />;
  }

  if (isError || !isSuccess) {
    <CookieError />;
  }

  if (!flagsReady) {
    return (
      <Box sx={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading text="Loading Unleash..." />
      </Box>
    );
  }

  return (
    <>
      <Routes>
        <Route element={<UnauthNavigator />}>
          <Route path={ROUTES.HOME} element={<HomePage />} />
        </Route>

        <Route path={ROUTES.VERIFICATION} element={<VerificationNavigator />}>
          <Route path={ROUTES.VERIFICATION_REQUEST} element={<VerificationRequestPage />} />
          <Route path={ROUTES.VERIFICATION_CONFIRM} element={<VerificationConfirmPage />} />
        </Route>

        <Route path={ROUTES.AUTH} element={<UnauthNavigator />}>
          <Route path={ROUTES.AUTH_LOGIN} element={<AuthLoginPage />} />
          <Route path={ROUTES.AUTH_SIGNUP} element={<AuthSignupPage />} />
          <Route path={ROUTES.AUTH_FORGOT_PASSWORD} element={<AuthForgotPasswordPage />} />
          <Route path={ROUTES.AUTH_RESET_PASSWORD} element={<AuthResetPasswordPage />} />
        </Route>

        <Route path={ROUTES.ORG_SLUG} element={<AuthNavigator />}>
          {dashboard && (
            <Route path={ROUTES.DASHBOARD} element={<DashboardNavigator />}>
              <Route path={ROUTES.DASHBOARD_BUILDING} element={<DashboardPage />} />
            </Route>
          )}

          <Route path={ROUTES.BUILDINGS} element={<BuildingsPage />} />

          <Route path={ROUTES.BUILDING_DETAILS} element={<BuildingDetailsNavigator />}>
            <Route path={ROUTES.ZONES_ACTIVE} element={<BuildingZonesActivePage />} />
            <Route path={ROUTES.ZONES_INACTIVE} element={<BuildingZonesInactivePage />} />
          </Route>

          <Route path={ROUTES.ZONE_CLEANING_HISTORY} element={<ZoneCleaningHistoryPage />} />

          <Route path={ROUTES.ZONES} element={<ZonesNavigator />}>
            <Route path={ROUTES.ZONES_ACTIVE} element={<ZonesActivePage />} />
            <Route path={ROUTES.ZONES_INACTIVE} element={<ZonesInactivePage />} />
          </Route>

          {serviceInterruptions && <Route path={ROUTES.SERVICE_INTERRUPTIONS} element={<ServiceInterruptionsPage />} />}

          <Route path={ROUTES.FEEDBACK} element={<FeedbackPage />} />
          <Route path={ROUTES.REPORTS} element={<ReportsPage />} />

          {process.env.NODE_ENV !== 'production' && <Route path={ROUTES.PDF_VIEW} element={<PdfViewPage />} />}

          {isAdmin && (
            <>
              <Route path={ROUTES.TEAM} element={<TeamPage />} />
              <Route path={ROUTES.BUILDING_CREATE} element={<BuildingCreatePage />} />
              <Route path={ROUTES.CART} element={<CartPageV2 />} />
              <Route path={ROUTES.EMPLOYEES} element={<EmployeesPage />} />
              <Route path={ROUTES.ORDER_DECALS} element={<OrderDecalsPage />} />
              <Route path={ROUTES.REVIEW_CART} element={<ReviewCartPage />} />
            </>
          )}

          <Route path={ROUTES.PROFILE} element={<ProfileNavigator />}>
            <Route path={ROUTES.PROFILE_USER_SETTINGS} element={<ProfileUserSettingsPage />} />
            <Route path={ROUTES.PROFILE_NOTIFICATIONS} element={<ProfileNotificationsPage />} />
            <Route path={ROUTES.PROFILE_PASSWORD} element={<ProfilePasswordPage />} />
            {isAdmin && <Route path={ROUTES.COMPANY_SETTINGS} element={<ProfileCompanySettingsPage />} />}
            <Route path={ROUTES.ZONE_CATEGORIES} element={<ZoneCategoriesPage />} />
            {isAdmin && <Route path={ROUTES.PROFILE_INVITE_USERS} element={<ProfileInviteUsersPage />} />}
            {isAdmin && <Route path={ROUTES.PROFILE_BILLING} element={<ProfileBillingPage />} />}
          </Route>
        </Route>

        <Route path={ROUTES.SUPERVISOR} element={<SupervisorNavigator />}>
          <Route path={ROUTES.SUSPEND_SERVICE} element={<SuspendServicePage />} />
          <Route path={ROUTES.SUSPEND_SERVICE_SUCCESS} element={<SuspendServiceSuccessPage />} />
        </Route>

        <Route path={ROUTES.CODE} element={<CodeNavigator />}>
          <Route path={ROUTES.CODE_PAGE} element={<CodePage />} />
          {employeeCode && <Route path={ROUTES.CODE_EMPLOYEE_ACCESS} element={<EmployeePage />} />}
        </Route>
      </Routes>

      <ToastContainer theme="colored" hideProgressBar closeOnClick position={'bottom-left'} bodyClassName="text-14" />
    </>
  );
};

const CookieLoading = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Loading text="Cleancode initialization..." />
    </Box>
  );
};
